<template>
    <Head :title="`Incident ${incident.incident_number}`" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="mb-6">
            <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                    <div>
                        <div class="flex items-center gap-3">
                            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ incident.incident_number }}</h1>
                            <div class="flex items-center space-x-2">
                                <span class="text-sm text-gray-600 dark:text-gray-400 font-medium">Status:</span>
                                <span :class="getStatusClass(incident.status)">
                                    {{ incident.status_label }}
                                </span>
                            </div>
                        </div>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">{{ incident.title }}</p>
                    </div>
                    <InfoButton title="Incident Details & Management" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                            The Incident Details page is your central hub for managing incidents and near misses through their entire lifecycle. From initial reporting to triage, investigation, corrective actions, and closure, this page provides all tools needed to track progress, manage investigations, document findings, and ensure proper incident resolution.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                            • <strong>Workflow Actions:</strong> Available actions change based on status - Open incidents can be triaged, Under Investigation incidents have investigation workspace access, and Closed incidents are locked to maintain resolution integrity<br>
                            • <strong>Triage:</strong> Managers assess severity and decide on no action, quick fix, or full investigation<br>
                            • <strong>Investigation:</strong> Team members collaborate to identify root causes and document findings<br>
                            • <strong>Corrective Actions:</strong> Track implementation of preventive and corrective measures<br>
                            • <strong>Activity Log:</strong> Complete audit trail of all changes, status updates, and actions taken
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                            • <strong>Status badges</strong> provide visual cues for incident severity and progress<br>
                            • <strong>SLA tracking</strong> alerts you to breached response or investigation deadlines<br>
                            • <strong>Anonymous reporting</strong> protects reporter identity while maintaining accountability<br>
                            • <strong>Investigation workspace</strong> enables collaborative root cause analysis<br>
                            • <strong>Activity log</strong> is automatically maintained for compliance and audits
                        </p>
                    </InfoButton>
                </div>
                <div class="flex items-center space-x-3">
                    <button
                        @click="showPrintPdfDialog = true"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md font-semibold text-xs uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Print PDF
                    </button>
                    <Link
                        :href="route('incidents.index')"
                        class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </Link>
                </div>
            </div>
        </div>

        <!-- Badge Info -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4 flex-wrap gap-2">
                    <div class="flex items-center space-x-2">
                        <span class="text-sm text-gray-600 dark:text-gray-400 font-medium">Type:</span>
                        <span v-if="incident.is_near_miss" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                            Near Miss
                        </span>
                        <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                            Incident
                        </span>
                    </div>
                    <div v-if="incident.severity" class="flex items-center space-x-2">
                        <span class="text-sm text-gray-600 dark:text-gray-400 font-medium">Severity:</span>
                        <span :class="getSeverityClass(incident.severity)">
                            {{ incident.severity }}
                        </span>
                    </div>
                    <div v-if="incident.priority" class="flex items-center space-x-2">
                        <span class="text-sm text-gray-600 dark:text-gray-400 font-medium">Priority:</span>
                        <span :class="getPriorityClass(incident.priority)">
                            {{ incident.priority }}
                        </span>
                    </div>
                    <span v-if="incident.sla_response_breached || incident.sla_investigation_breached" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 animate-pulse">
                        ⚠️ SLA Breach
                    </span>
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">
                    Reported: {{ formatDate(incident.reported_at) }}
                </div>
            </div>
        </div>

        <!-- Incident Information Card -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Incident Information</h2>
            <div class="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-1">Title</p>
                <p class="text-base font-semibold text-gray-900 dark:text-white">{{ incident.title }}</p>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Reported By</p>
                    <p class="font-medium text-gray-900 dark:text-white">
                        <span v-if="incident.is_anonymous">Anonymous</span>
                        <span v-else>{{ incident.reported_by?.full_name || 'N/A' }}</span>
                    </p>
                </div>
                <div>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Reported At</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(incident.reported_at) }}</p>
                </div>
                <div>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Discovered At</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(incident.discovered_at) }}</p>
                </div>
                <div v-if="incident.type">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Incident Type</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ incident.type.name }}</p>
                </div>
                <div v-if="incident.location">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Location</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ incident.location.name }}</p>
                </div>
                <div v-if="incident.triage_completed_by">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Triaged By</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ incident.triage_completed_by.full_name }}</p>
                </div>
                <div v-if="incident.closed_by">
                    <p class="text-sm text-gray-600 dark:text-gray-400">Closed By</p>
                    <p class="font-medium text-gray-900 dark:text-white">{{ incident.closed_by.full_name }}</p>
                </div>
            </div>
            <div v-if="incident.people_involved && incident.people_involved.length > 0" class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">People Involved</p>
                <div class="text-gray-900 dark:text-white">
                    <p v-for="(person, index) in incident.people_involved" :key="index" class="text-sm mb-1">
                        {{ person.name }}<span v-if="person.role">, {{ person.role }}</span><span v-if="person.contact">, {{ person.contact }}</span>
                    </p>
                </div>
            </div>
            <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">Description</p>
                <p class="text-gray-900 dark:text-white whitespace-pre-wrap">{{ incident.description }}</p>
            </div>
        </div>

        <!-- Triage Assessment -->
        <div v-if="(canViewFullDetails || isInvestigationMember || isQuickFixAssignee) && incident.manager_assessment" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Triage Assessment</h2>
            <div class="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border-l-4 border-blue-400 dark:border-blue-500">
                <p class="text-sm text-blue-800 dark:text-blue-300 whitespace-pre-wrap">{{ incident.manager_assessment }}</p>
            </div>
        </div>

        <!-- Quick Fix Action - Completed (shown when closed via quick fix) -->
        <div v-if="(canViewFullDetails || isQuickFixAssignee) && incident.status === 'closed' && incident.triage_decision === 'quick_fix'" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Fix Action - Completed</h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p class="text-xs text-gray-500 mb-1">Assigned To</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ incident.quick_fix_assigned_to?.full_name || 'Unassigned' }}</p>
                </div>
                <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p class="text-xs text-gray-500 mb-1">Due Date</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ incident.quick_fix_due_date ? formatDate(incident.quick_fix_due_date) : 'Not set' }}</p>
                </div>
            </div>

            <div v-if="incident.quick_fix_action_description" class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border-l-4 border-yellow-400 dark:border-yellow-500">
                <p class="text-sm font-medium text-yellow-900 dark:text-yellow-300 mb-1">Action Description</p>
                <p class="text-sm text-yellow-800 dark:text-yellow-300 whitespace-pre-wrap">{{ incident.quick_fix_action_description }}</p>
            </div>

            <div v-if="incident.quick_fix_completion_notes" class="mb-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border-l-4 border-green-400 dark:border-green-500">
                <div class="flex items-center justify-between mb-1">
                    <p class="text-sm font-medium text-green-900 dark:text-green-300">Completion Notes</p>
                    <p class="text-xs text-green-700 dark:text-green-400">Completed: {{ incident.quick_fix_completed_at ? formatDate(incident.quick_fix_completed_at) : 'Not set' }}</p>
                </div>
                <p class="text-sm text-green-800 dark:text-green-300 whitespace-pre-wrap">{{ incident.quick_fix_completion_notes }}</p>
            </div>
        </div>

        <!-- Status-Based Action Buttons -->
        <!-- Open Status - Triage -->
        <div v-if="canViewFullDetails && incident.status === 'open' && canTriage" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions</h2>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">This incident is awaiting triage. Review the details and make a decision on how to proceed.</p>
            <div class="flex space-x-3">
                <Link
                    :href="route('incidents.triage', incident.id)"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 font-semibold"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                    </svg>
                    Start Triage
                </Link>
            </div>
        </div>

        <!-- Assigned (Quick Fix) Status -->
        <div v-if="(canViewFullDetails || isQuickFixAssignee) && incident.status === 'assigned' && incident.triage_decision === 'quick_fix'" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Quick Fix Action</h2>
                <div v-if="canTriage" class="flex space-x-2">
                    <button
                        @click="showChangeAssigneeDialog = true"
                        class="inline-flex items-center px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white text-sm rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 font-semibold"
                    >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                        </svg>
                        Change Assignee
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p class="text-xs text-gray-500 mb-1">Assigned To</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ incident.quick_fix_assigned_to?.full_name || 'Unassigned' }}</p>
                </div>
                <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p class="text-xs text-gray-500 mb-1">Due Date</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ incident.quick_fix_due_date ? formatDate(incident.quick_fix_due_date) : 'Not set' }}</p>
                </div>
            </div>

            <div v-if="incident.quick_fix_action_description" class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border-l-4 border-yellow-400 dark:border-yellow-500">
                <p class="text-sm font-medium text-yellow-900 dark:text-yellow-300 mb-1">Action Required</p>
                <p class="text-sm text-yellow-800 dark:text-yellow-300 whitespace-pre-wrap">{{ incident.quick_fix_action_description }}</p>
            </div>

            <div v-if="incident.quick_fix_review_notes" class="mb-4 p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg border-l-4 border-orange-400 dark:border-orange-500">
                <p class="text-sm font-medium text-orange-900 dark:text-orange-300 mb-1">Review Feedback - Action Needs Rework</p>
                <p class="text-sm text-orange-800 dark:text-orange-300 whitespace-pre-wrap">{{ incident.quick_fix_review_notes }}</p>
            </div>

            <div v-if="isQuickFixAssignee || canEdit || canTriage" class="flex space-x-3">
                <button
                    @click="showCompleteQuickFixDialog = true"
                    class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 font-semibold"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Complete Action
                </button>
            </div>
        </div>

        <!-- Action Completed (Awaiting Review) Status -->
        <div v-if="(canViewFullDetails || isQuickFixAssignee) && incident.status === 'action_completed' && incident.triage_decision === 'quick_fix'" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Fix Action - Awaiting Review</h2>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p class="text-xs text-gray-500 mb-1">Assigned To</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ incident.quick_fix_assigned_to?.full_name || 'Unassigned' }}</p>
                </div>
                <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p class="text-xs text-gray-500 mb-1">Due Date</p>
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ incident.quick_fix_due_date ? formatDate(incident.quick_fix_due_date) : 'Not set' }}</p>
                </div>
            </div>

            <div v-if="incident.quick_fix_action_description" class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border-l-4 border-yellow-400 dark:border-yellow-500">
                <p class="text-sm font-medium text-yellow-900 dark:text-yellow-300 mb-1">Action Description</p>
                <p class="text-sm text-yellow-800 dark:text-yellow-300 whitespace-pre-wrap">{{ incident.quick_fix_action_description }}</p>
            </div>

            <div v-if="incident.quick_fix_review_notes" class="mb-4 p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg border-l-4 border-orange-400 dark:border-orange-500">
                <p class="text-sm font-medium text-orange-900 dark:text-orange-300 mb-1">Review Feedback - Action Needs Rework</p>
                <p class="text-sm text-orange-800 dark:text-orange-300 whitespace-pre-wrap">{{ incident.quick_fix_review_notes }}</p>
            </div>

            <div v-if="incident.quick_fix_completion_notes" class="mb-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border-l-4 border-green-400 dark:border-green-500">
                <div class="flex items-center justify-between mb-1">
                    <p class="text-sm font-medium text-green-900 dark:text-green-300">Completion Notes</p>
                    <p class="text-xs text-green-700 dark:text-green-400">Completed: {{ incident.quick_fix_completed_at ? formatDate(incident.quick_fix_completed_at) : 'Not set' }}</p>
                </div>
                <p class="text-sm text-green-800 dark:text-green-300 whitespace-pre-wrap">{{ incident.quick_fix_completion_notes }}</p>
            </div>

            <!-- Review Actions -->
            <div v-if="canTriage || canEdit" class="flex space-x-3">
                <button
                    @click="showCloseQuickFixDialog = true"
                    class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 font-semibold"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Close Incident
                </button>
                <button
                    @click="showRedoFixDialog = true"
                    class="inline-flex items-center px-4 py-2 bg-orange-600 dark:bg-orange-500 text-white rounded-md hover:bg-orange-700 dark:hover:bg-orange-600 font-semibold"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                    </svg>
                    Redo Fix
                </button>
            </div>
        </div>

        <!-- Under Investigation Status -->
        <div v-if="(canViewFullDetails || isInvestigationMember) && ((incident.status === 'under_investigation') || (incident.status === 'investigation_completed') || (incident.status === 'assigned' && incident.triage_decision === 'investigation')) && (isInvestigationMember || canTriage)" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Investigation</h2>
                <div v-if="canTriage" class="flex space-x-2">
                    <button
                        @click="showChangeInvestigationAssigneeDialog = true"
                        class="inline-flex items-center px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white text-sm rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 font-semibold"
                    >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                        </svg>
                        Change Investigator
                    </button>
                </div>
            </div>

            <div class="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Investigator</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ incident.investigation_assigned_to?.full_name || 'Unassigned' }}
                </p>
                <p v-if="investigationStats" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ investigationStats.notes }} notes • {{ investigationStats.evidence }} evidence • {{ investigationStats.root_causes }} root causes
                </p>
            </div>

            <div v-if="incident.investigation_action_description" class="mb-4 p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg border-l-4 border-purple-400 dark:border-purple-500">
                <p class="text-sm font-medium text-purple-900 dark:text-purple-300 mb-1">Investigation Instructions</p>
                <p class="text-sm text-purple-800 dark:text-purple-300 whitespace-pre-wrap">{{ incident.investigation_action_description }}</p>
            </div>

            <!-- Reopen Reasons - Shows all review notes from submissions with reopen reasons -->
            <div v-if="incident.investigation_submissions && incident.investigation_submissions.length > 0" class="space-y-3 mb-4">
                <div v-for="(submission, index) in incident.investigation_submissions" :key="submission.id"
                     class="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border-l-4 border-yellow-400 dark:border-yellow-500">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-1">
                                Review Note #{{ index + 1 }}
                            </p>
                            <p class="text-sm text-yellow-800 dark:text-yellow-300 whitespace-pre-wrap">{{ submission.reopen_reason }}</p>
                            <p v-if="submission.reviewed_at" class="text-xs text-yellow-700 dark:text-yellow-400 mt-2">
                                Reviewed at {{ formatDate(submission.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="incident.status === 'assigned' && incident.triage_decision === 'investigation'" class="flex justify-end">
                <!-- Show "Start Investigation" button when status is 'assigned' and it's an investigation -->
                <button
                    @click="startInvestigation"
                    class="inline-flex items-center px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-600 font-semibold"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Start Investigation
                </button>
            </div>

            <!-- Investigation Workspace - Shows when status is 'under_investigation' or 'investigation_completed' -->
            <div v-if="incident.status === 'under_investigation' || incident.status === 'investigation_completed'" class="mt-6 space-y-6">
                <!-- Investigation Items (Notes & Evidence) Section -->
                <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div class="flex justify-between items-center mb-4">
                        <h3 class="text-md font-semibold text-gray-900 dark:text-white">Investigation Notes & Evidence</h3>
                    </div>

                    <!-- Existing Items Table -->
                    <div v-if="incident.investigation_items && incident.investigation_items.length > 0" class="mb-4 overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead class="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added By</th>
                                    <th v-if="incident.status === 'under_investigation'" class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr v-for="item in incident.investigation_items" :key="item.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td class="px-4 py-3 whitespace-nowrap align-top">
                                        <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                                            :class="{
                                                'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300': item.item_type === 'investigation_note',
                                                'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300': item.item_type === 'evidence',
                                                'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300': item.item_type === 'witness_statement'
                                            }">
                                            {{ item.item_type === 'investigation_note' ? 'Note' : item.item_type === 'evidence' ? 'Evidence' : 'Witness' }}
                                        </span>
                                    </td>
                                    <td class="px-4 py-3 align-top">
                                        <p class="text-sm font-medium text-gray-900 dark:text-white">{{ item.title }}</p>
                                    </td>
                                    <td class="px-4 py-3 align-top">
                                        <p class="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ item.description || '-' }}</p>
                                    </td>
                                    <td class="px-4 py-3 whitespace-nowrap align-top">
                                        <div v-if="item.attachment" class="flex items-center space-x-2">
                                            <a :href="route('incidents.investigation.download', { incident: props.incident.id, item: item.id })"
                                               class="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                               target="_blank"
                                            >
                                                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                </svg>
                                                {{ item.attachment.file_name }}
                                            </a>
                                            <button
                                                v-if="(canTriage || (isInvestigationMember && item.user_id === page.props.auth?.user?.id)) && incident.status === 'under_investigation'"
                                                @click="deleteInvestigationFile(item.id)"
                                                class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                title="Delete File"
                                            >
                                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                </svg>
                                            </button>
                                        </div>
                                        <span v-else class="text-sm text-gray-400 dark:text-gray-500">-</span>
                                    </td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 align-top">
                                        {{ item.user?.full_name }}
                                    </td>
                                    <td v-if="incident.status === 'under_investigation'" class="px-4 py-3 whitespace-nowrap text-right text-sm align-top">
                                        <div v-if="canTriage || (isInvestigationMember && item.user_id === page.props.auth?.user?.id)" class="flex justify-end space-x-2">
                                            <button
                                                @click="openEditItemModal(item)"
                                                class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                title="Edit"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                                </svg>
                                            </button>
                                            <button
                                                @click="deleteInvestigationItem(item.id)"
                                                class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                title="Delete"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Add Investigation Item Form - Compact Row Layout -->
                    <div v-if="(isInvestigationMember || canTriage) && incident.status === 'under_investigation'" class="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div class="grid grid-cols-12 gap-3 items-start">
                            <div class="col-span-2">
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                                <select
                                    v-model="investigationItemForm.item_type"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
                                >
                                    <option value="investigation_note">Note</option>
                                    <option value="evidence">Evidence</option>
                                    <option value="witness_statement">Witness</option>
                                </select>
                            </div>
                            <div class="col-span-3">
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                                <input
                                    v-model="investigationItemForm.title"
                                    type="text"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
                                    placeholder="Brief title..."
                                />
                            </div>
                            <div class="col-span-4">
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <textarea
                                    v-model="investigationItemForm.description"
                                    rows="2"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm resize-y"
                                    placeholder="Description..."
                                ></textarea>
                            </div>
                            <div class="col-span-2">
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">File (Optional)</label>
                                <input
                                    @change="handleFileUpload"
                                    type="file"
                                    class="w-full text-xs text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                                />
                            </div>
                            <div class="col-span-1 flex items-end">
                                <button
                                    @click="addInvestigationItem"
                                    :disabled="!investigationItemForm.title || investigationItemForm.processing"
                                    class="w-full px-3 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 text-sm font-medium"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Root Causes Section -->
                <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <div class="flex justify-between items-center mb-4">
                        <h3 class="text-md font-semibold text-gray-900 dark:text-white">Root Causes</h3>
                    </div>

                    <!-- Existing Root Causes Table -->
                    <div v-if="incident.root_causes && incident.root_causes.length > 0" class="mb-4 overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead class="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added By</th>
                                    <th v-if="incident.status === 'under_investigation'" class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                <tr v-for="cause in incident.root_causes" :key="cause.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td class="px-4 py-3 whitespace-nowrap align-top">
                                        <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                                            :class="{
                                                'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300': cause.cause_type === 'primary',
                                                'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300': cause.cause_type === 'contributing'
                                            }">
                                            {{ cause.cause_type === 'primary' ? 'Primary' : 'Contributing' }}
                                        </span>
                                    </td>
                                    <td class="px-4 py-3 align-top">
                                        <p class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{{ cause.description }}</p>
                                    </td>
                                    <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 align-top">
                                        {{ cause.created_by?.full_name }}
                                    </td>
                                    <td v-if="incident.status === 'under_investigation'" class="px-4 py-3 whitespace-nowrap text-right text-sm align-top">
                                        <div v-if="canTriage || (isInvestigationMember && cause.created_by?.id === page.props.auth?.user?.id)" class="flex justify-end space-x-2">
                                            <button
                                                @click="openEditRootCauseModal(cause)"
                                                class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                title="Edit"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                                </svg>
                                            </button>
                                            <button
                                                @click="deleteRootCause(cause.id)"
                                                class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                title="Delete"
                                            >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Add Root Cause Form - Compact Row Layout -->
                    <div v-if="(isInvestigationMember || canTriage) && incident.status === 'under_investigation'" class="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div class="grid grid-cols-12 gap-3 items-start">
                            <div class="col-span-2">
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                                <select
                                    v-model="rootCauseForm.cause_type"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm"
                                >
                                    <option value="primary">Primary</option>
                                    <option value="contributing">Contributing</option>
                                </select>
                            </div>
                            <div class="col-span-9">
                                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <textarea
                                    v-model="rootCauseForm.description"
                                    rows="2"
                                    class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm resize-y"
                                    placeholder="Describe the root cause..."
                                ></textarea>
                            </div>
                            <div class="col-span-1 flex items-end">
                                <button
                                    @click="addRootCause"
                                    :disabled="!rootCauseForm.description || rootCauseForm.processing"
                                    class="w-full px-3 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 text-sm font-medium"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Submit Investigation Button -->
                <div v-if="isInvestigationMember && incident.status === 'under_investigation'" class="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <button
                        @click="showSubmitInvestigationDialog = true"
                        class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 font-semibold"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Submit Investigation for Review
                    </button>
                </div>

                <!-- Investigation Review Actions (Manager Only) -->
                <div v-if="canClose && incident.status === 'investigation_completed'" class="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Review Investigation</h4>
                    <div class="flex flex-wrap gap-3">
                        <button
                            @click="handleRequiresFurtherInvestigation"
                            class="inline-flex items-center px-4 py-2 bg-orange-600 dark:bg-orange-500 text-white rounded-md hover:bg-orange-700 dark:hover:bg-orange-600 font-medium"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z"></path>
                            </svg>
                            Requires Further Investigation
                        </button>
                        <button
                            @click="handleApproveAndClose"
                            class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 font-medium"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            Approve & Close Incident
                        </button>
                        <button
                            @click="handleApproveAndAddCorrectiveAction"
                            class="inline-flex items-center px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-600 font-medium"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                            </svg>
                            Approve & Add Corrective Action
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Investigation Results - Shows investigation results when closed or in corrective action phase -->
        <div v-if="(canViewFullDetails || isInvestigationMember) && (incident.status === 'closed' || incident.status === 'pending_corrective_action' || incident.status === 'correction_in_progress' || incident.status === 'correction_in_review') && incident.triage_decision === 'investigation'" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Investigation Results</h2>

            <div class="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Investigated By</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ incident.investigation_assigned_to?.full_name || 'Unassigned' }}
                </p>
                <p v-if="investigationStats" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ investigationStats.notes }} notes • {{ investigationStats.evidence }} evidence • {{ investigationStats.root_causes }} root causes
                </p>
            </div>

            <!-- Investigation Notes & Evidence -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
                <h3 class="text-md font-semibold text-gray-900 dark:text-white mb-4">Investigation Notes & Evidence</h3>

                <div v-if="incident.investigation_items && incident.investigation_items.length > 0" class="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added By</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="item in incident.investigation_items" :key="item.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-4 py-3 whitespace-nowrap align-top">
                                    <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                                        :class="{
                                            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300': item.item_type === 'investigation_note',
                                            'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300': item.item_type === 'evidence',
                                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300': item.item_type === 'witness_statement'
                                        }">
                                        {{ item.item_type === 'investigation_note' ? 'Note' : item.item_type === 'evidence' ? 'Evidence' : 'Witness' }}
                                    </span>
                                </td>
                                <td class="px-4 py-3 align-top">
                                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ item.title }}</p>
                                </td>
                                <td class="px-4 py-3 align-top">
                                    <p class="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ item.description || '-' }}</p>
                                </td>
                                <td class="px-4 py-3 whitespace-nowrap align-top">
                                    <div v-if="item.attachment" class="flex items-center space-x-2">
                                        <a :href="route('incidents.investigation.download', { incident: props.incident.id, item: item.id })"
                                           class="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                           target="_blank"
                                        >
                                            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                            </svg>
                                            {{ item.attachment.file_name }}
                                        </a>
                                    </div>
                                    <span v-else class="text-sm text-gray-400 dark:text-gray-500">-</span>
                                </td>
                                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 align-top">
                                    {{ item.user?.full_name }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-else class="text-sm text-gray-500 dark:text-gray-400">No investigation items recorded.</p>
            </div>

            <!-- Root Causes -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 class="text-md font-semibold text-gray-900 dark:text-white mb-4">Root Causes</h3>

                <div v-if="incident.root_causes && incident.root_causes.length > 0" class="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Added By</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="cause in incident.root_causes" :key="cause.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-4 py-3 whitespace-nowrap align-top">
                                    <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                                        :class="{
                                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300': cause.cause_type === 'primary',
                                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300': cause.cause_type === 'contributing'
                                        }">
                                        {{ cause.cause_type === 'primary' ? 'Primary' : 'Contributing' }}
                                    </span>
                                </td>
                                <td class="px-4 py-3 align-top">
                                    <p class="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{{ cause.description }}</p>
                                </td>
                                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 align-top">
                                    {{ cause.created_by?.full_name }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-else class="text-sm text-gray-500 dark:text-gray-400">No root causes identified.</p>
            </div>
        </div>

        <!-- Corrective Actions Status -->
        <div v-if="(canViewFullDetails || hasAssignedActions) && (['pending_corrective_action', 'correction_in_progress', 'correction_in_review'].includes(incident.status) || (incident.status === 'closed' && incident.closed_with_corrections))" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <div class="flex items-start justify-between mb-4">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Corrective Actions</h2>
                <div v-if="canTriage && incident.status !== 'closed'" class="flex space-x-2">
                    <button
                        @click="showChangeCorrectiveActionAssigneeDialog = true"
                        class="inline-flex items-center px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white text-sm rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 font-semibold"
                    >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                        </svg>
                        Reassign
                    </button>
                </div>
            </div>

            <div class="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Assigned to</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ incident.corrective_action_assigned_to?.full_name || 'Unassigned' }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Due: {{ incident.corrective_action_due_date ? formatDate(incident.corrective_action_due_date) : 'No due date' }} • {{ incident.corrective_actions?.length || 0 }} action(s)
                </p>
            </div>

            <!-- Corrective Action Review Note -->
            <div v-if="incident.corrective_action_review_note && incident.status !== 'closed'" class="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border-l-4 border-yellow-400 dark:border-yellow-500">
                <div class="flex items-start">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                    <div class="ml-3 flex-1">
                        <p class="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-1">
                            Review Note
                        </p>
                        <p class="text-sm text-yellow-800 dark:text-yellow-300 whitespace-pre-wrap">{{ incident.corrective_action_review_note }}</p>
                    </div>
                </div>
            </div>

            <!-- Corrective Actions Table -->
            <div v-if="incident.corrective_actions && incident.corrective_actions.length > 0" class="mb-6">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th v-if="incident.status !== 'closed'" class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="action in incident.corrective_actions" :key="action.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-4 py-3 whitespace-nowrap">
                                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                        :class="{
                                            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300': action.action_type === 'corrective',
                                            'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300': action.action_type === 'preventive'
                                        }">
                                        {{ action.action_type === 'corrective' ? 'Corrective' : 'Preventive' }}
                                    </span>
                                </td>
                                <td class="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{{ action.title }}</td>
                                <td class="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                    {{ action.description }}
                                    <div v-if="action.action_taken_description" class="mt-2 p-2 bg-green-50 dark:bg-green-900/30 rounded text-xs text-green-800 dark:text-green-300 border-l-2 border-green-400 dark:border-green-500">
                                        <span class="font-medium">Action Taken:</span> {{ action.action_taken_description }}
                                    </div>
                                </td>
                                <td class="px-4 py-3 whitespace-nowrap">
                                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                        :class="{
                                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300': action.status === 'pending',
                                            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300': action.status === 'in_progress',
                                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300': action.status === 'completed'
                                        }">
                                        {{ action.status.replace('_', ' ').toUpperCase() }}
                                    </span>
                                </td>
                                <td v-if="incident.status !== 'closed'" class="px-4 py-3 whitespace-nowrap text-right text-sm">
                                    <button
                                        v-if="action.status === 'pending'"
                                        @click="startCorrectiveAction(action.id)"
                                        class="inline-flex items-center px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white text-xs rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 font-semibold"
                                        title="Start Work"
                                    >
                                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Start Work
                                    </button>
                                    <button
                                        v-if="action.status === 'in_progress'"
                                        @click="openCompleteActionDialog(action)"
                                        class="inline-flex items-center px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white text-xs rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 font-semibold"
                                        title="Mark as Completed"
                                    >
                                        <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                        Mark as Completed
                                    </button>
                                    <span v-if="action.status === 'completed'" class="text-xs text-green-600 dark:text-green-400 font-medium">
                                        ✓ Completed
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Add Corrective Action Form -->
            <div v-if="incident.status !== 'closed' && incident.status !== 'correction_in_review'" class="mb-6 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">Add New Action</h3>
                <div class="grid grid-cols-12 gap-3 items-start">
                    <div class="col-span-2">
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                        <select
                            v-model="correctiveActionForm.action_type"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                        >
                            <option value="">Select</option>
                            <option value="corrective">Corrective</option>
                            <option value="preventive">Preventive</option>
                        </select>
                    </div>
                    <div class="col-span-4">
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                        <input
                            v-model="correctiveActionForm.title"
                            type="text"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                            placeholder="Brief title..."
                        />
                    </div>
                    <div class="col-span-5">
                        <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                        <textarea
                            v-model="correctiveActionForm.description"
                            rows="1"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm resize-y"
                            placeholder="What needs to be done..."
                        ></textarea>
                    </div>
                    <div class="col-span-1 flex items-end">
                        <button
                            @click="addCorrectiveAction"
                            :disabled="!correctiveActionForm.action_type || !correctiveActionForm.title || !correctiveActionForm.description || correctiveActionForm.processing"
                            class="w-full px-3 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 text-sm font-medium"
                        >
                            Add
                        </button>
                    </div>
                </div>
            </div>

            <!-- Submit for Review Button -->
            <div v-if="allCorrectiveActionsCompleted && incident.status !== 'correction_in_review' && incident.status !== 'closed'" class="flex justify-start">
                <button
                    @click="submitCorrectiveActionsForReview"
                    class="inline-flex items-center px-6 py-2.5 bg-green-600 dark:bg-green-500 text-white text-sm font-semibold rounded-md hover:bg-green-700 dark:hover:bg-green-600"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Submit for Review
                </button>
            </div>

            <!-- Correction Review Actions -->
            <div v-if="incident.status === 'correction_in_review' && canTriage" class="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-3">Review Corrective Actions</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">All corrective actions have been completed and submitted for review. Please review and take appropriate action.</p>
                <div class="flex space-x-3">
                    <button
                        @click="showRequireFurtherWorkDialog = true"
                        class="inline-flex items-center px-4 py-2 bg-yellow-600 dark:bg-yellow-500 text-white text-sm font-semibold rounded-md hover:bg-yellow-700 dark:hover:bg-yellow-600"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                        </svg>
                        Require Further Work
                    </button>
                    <button
                        @click="showApproveAndCloseCorrectiveActionsDialog = true"
                        class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white text-sm font-semibold rounded-md hover:bg-green-700 dark:hover:bg-green-600"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Approve and Close Incident
                    </button>
                </div>
            </div>
        </div>

        <!-- Closed Status -->
        <div v-if="incident.status === 'closed' && ((canViewFullDetails && canTriage) || isInvestigationMember || isReporter || hasAssignedActions || isQuickFixAssignee)" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Incident Closed</h2>
            <div class="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-l-4 border-gray-400 dark:border-gray-600">
                <p class="text-sm text-gray-700 dark:text-gray-300">
                    This incident has been resolved and closed. To maintain the integrity of the incident resolution process and ensure accurate record-keeping, closed incidents cannot be modified or reopened. If additional work or follow-up action is required, please create a new incident referencing this one, ensuring proper tracking and documentation of all related activities.
                </p>
            </div>

            <!-- Closure Reason Display (for no_action decisions) -->
            <div v-if="incident.no_action_reason" class="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border-l-4 border-blue-400 dark:border-blue-500">
                <p class="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">Closure Reason</p>
                <p class="text-sm text-blue-800 dark:text-blue-300">{{ formatClosureReason(incident.no_action_reason) }}</p>
            </div>

            <!-- Closure Notes Display -->
            <div v-if="incident.closure_notes" class="mb-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border-l-4 border-green-400 dark:border-green-500">
                <p class="text-sm font-medium text-green-900 dark:text-green-300 mb-1">Closure Notes</p>
                <p class="text-sm text-green-800 dark:text-green-300 whitespace-pre-wrap">{{ incident.closure_notes }}</p>
            </div>
        </div>


        <!-- SLA Information Card -->
        <div v-if="canViewFullDetails || isInvestigationMember" class="bg-white dark:bg-gray-800 rounded-lg shadow mt-6 mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-3">
                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">SLA Information</h3>
                    <InfoButton title="Understanding SLA Metrics" variant="default">
                        <p>
                            <strong class="text-secondary-900 dark:text-white">What is SLA?</strong><br>
                            Service Level Agreement (SLA) defines the expected time to respond to and resolve incidents.
                            This section tracks performance against these commitments.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Response SLA</strong><br>
                            The maximum time allowed to triage and acknowledge the incident. This is met when
                            the incident status changes from "open" to any other status (triaged). Response SLA ensures timely acknowledgment of incidents.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Investigation/Resolution SLA</strong><br>
                            The maximum time allowed to fully resolve and close the incident. This measures the total
                            time from creation to closure. Resolution SLA ensures incidents are resolved within agreed timeframes.
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">SLA Status Indicators</strong><br>
                            • <strong>Met (Green):</strong> SLA deadline met - good performance<br>
                            • <strong>Breached (Red):</strong> Deadline passed - SLA violated<br>
                            • <strong>Pending:</strong> SLA not yet met, time remaining shown
                        </p>
                        <p>
                            <strong class="text-secondary-900 dark:text-white">Time Remaining</strong><br>
                            Shows how much time is left until the SLA deadline. Green checkmark (✓) indicates SLA was met,
                            red X (✗) indicates breach.
                        </p>
                    </InfoButton>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <!-- SLA Policy -->
                <div>
                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">SLA Policy</dt>
                    <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                        {{ incident.sla_policy?.name || 'N/A' }}
                    </dd>
                </div>

                <!-- Response SLA -->
                <div>
                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Response SLA</dt>
                    <dd class="mt-1 text-sm">
                        <div v-if="incident.sla_response_due_at" class="space-y-1">
                            <div :class="incident.sla_response_breached ? 'text-red-600 font-semibold' : incident.sla_response_met_at ? 'text-green-600' : 'text-gray-900 dark:text-white'">
                                Due: {{ formatDateTime(incident.sla_response_due_at) }}
                            </div>
                            <div v-if="incident.sla_response_met_at" class="text-green-600 text-xs">
                                ✓ Met at {{ formatDateTime(incident.sla_response_met_at) }}
                            </div>
                            <div v-else-if="incident.sla_response_breached" class="text-red-600 text-xs font-semibold">
                                ✗ Breached
                            </div>
                            <div v-else class="text-gray-600 dark:text-gray-400 text-xs">
                                {{ getTimeRemaining(incident.sla_response_due_at) }}
                            </div>
                        </div>
                        <span v-else class="text-gray-400">Not set</span>
                    </dd>
                </div>

                <!-- Investigation/Resolution SLA -->
                <div>
                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Investigation/Resolution SLA</dt>
                    <dd class="mt-1 text-sm">
                        <div v-if="incident.sla_investigation_due_at" class="space-y-1">
                            <div :class="incident.sla_investigation_breached ? 'text-red-600 font-semibold' : incident.sla_investigation_met_at ? 'text-green-600' : 'text-gray-900 dark:text-white'">
                                Due: {{ formatDateTime(incident.sla_investigation_due_at) }}
                            </div>
                            <div v-if="incident.sla_investigation_met_at" class="text-green-600 text-xs">
                                ✓ Met at {{ formatDateTime(incident.sla_investigation_met_at) }}
                            </div>
                            <div v-else-if="incident.sla_investigation_breached" class="text-red-600 text-xs font-semibold">
                                ✗ Breached
                            </div>
                            <div v-else class="text-gray-600 dark:text-gray-400 text-xs">
                                {{ getTimeRemaining(incident.sla_investigation_due_at) }}
                            </div>
                        </div>
                        <span v-else class="text-gray-400">Not set</span>
                    </dd>
                </div>
            </div>
        </div>
        <!-- Activity Log Section -->
        <div v-if="canViewFullDetails" class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Activity Log</h2>

            <div v-if="logs && logs.length > 0" class="flow-root max-h-96 overflow-y-auto pr-2">
                <ul class="-mb-8">
                    <li v-for="(log, idx) in logs" :key="log.id">
                        <div class="relative pb-8">
                            <span v-if="idx !== logs.length - 1" class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true"></span>
                            <div class="relative flex space-x-3">
                                <div>
                                    <span class="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white dark:ring-gray-800" :class="getLogIconClass(log.action_type)">
                                        <svg class="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"></path>
                                        </svg>
                                    </span>
                                </div>
                                <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                    <div class="flex-1">
                                        <p class="text-sm font-medium text-gray-900 dark:text-white">{{ log.action_type_label || log.action_type }}</p>
                                        <p v-if="log.action_description" class="text-sm text-gray-500 dark:text-gray-400 mt-1 whitespace-pre-wrap">{{ log.action_description }}</p>
                                        <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                            by {{ log.user?.full_name || log.actor_name || 'System' }}
                                        </p>
                                    </div>
                                    <div class="text-right text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                                        <p>{{ formatDate(log.occurred_at) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            <div v-else class="text-center py-12">
                <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Activity</h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">No activity has been logged for this incident yet.</p>
            </div>
        </div>

        <!-- Change Assignee Dialog -->
        <Modal :show="showChangeAssigneeDialog" @close="showChangeAssigneeDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Change Quick Fix Assignee</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Select a new team member to assign this quick fix action:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assign To</label>
                    <select
                        v-model="changeAssigneeForm.quick_fix_assigned_to"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500': changeAssigneeForm.errors.quick_fix_assigned_to }"
                    >
                        <option value="">Select Team Member</option>
                        <option v-for="user in users" :key="user.id" :value="user.id">
                            {{ user.full_name }} ({{ user.email }})
                        </option>
                    </select>
                    <p v-if="changeAssigneeForm.errors.quick_fix_assigned_to" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ changeAssigneeForm.errors.quick_fix_assigned_to }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showChangeAssigneeDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="changeAssignee"
                        :disabled="changeAssigneeForm.processing || !changeAssigneeForm.quick_fix_assigned_to"
                        class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
                    >
                        Change Assignee
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Change Investigation Assignee Dialog -->
        <Modal :show="showChangeInvestigationAssigneeDialog" @close="showChangeInvestigationAssigneeDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Change Investigation Assignee</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Select a new team member to assign this investigation:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assign To</label>
                    <select
                        v-model="changeInvestigationAssigneeForm.investigation_assigned_to"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500': changeInvestigationAssigneeForm.errors.investigation_assigned_to }"
                    >
                        <option value="">Select Team Member</option>
                        <option v-for="member in users" :key="member.id" :value="member.id">
                            {{ member.full_name }}
                        </option>
                    </select>
                    <p v-if="changeInvestigationAssigneeForm.errors.investigation_assigned_to" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ changeInvestigationAssigneeForm.errors.investigation_assigned_to }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showChangeInvestigationAssigneeDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="changeInvestigationAssignee"
                        :disabled="changeInvestigationAssigneeForm.processing || !changeInvestigationAssigneeForm.investigation_assigned_to"
                        class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
                    >
                        Change Investigator
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Change Corrective Action Assignee Dialog -->
        <Modal :show="showChangeCorrectiveActionAssigneeDialog" @close="showChangeCorrectiveActionAssigneeDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Reassign Corrective Actions</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Select a new team member to assign all corrective actions:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assign To</label>
                    <select
                        v-model="changeCorrectiveActionAssigneeForm.corrective_action_assigned_to"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500': changeCorrectiveActionAssigneeForm.errors.corrective_action_assigned_to }"
                    >
                        <option value="">Select Team Member</option>
                        <option v-for="member in users" :key="member.id" :value="member.id">
                            {{ member.full_name }}
                        </option>
                    </select>
                    <p v-if="changeCorrectiveActionAssigneeForm.errors.corrective_action_assigned_to" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ changeCorrectiveActionAssigneeForm.errors.corrective_action_assigned_to }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showChangeCorrectiveActionAssigneeDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="changeCorrectiveActionAssignee"
                        :disabled="changeCorrectiveActionAssigneeForm.processing || !changeCorrectiveActionAssigneeForm.corrective_action_assigned_to"
                        class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
                    >
                        Reassign
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Complete Corrective Action Dialog -->
        <Modal :show="showCompleteActionDialog" @close="showCompleteActionDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Complete Corrective Action</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Describe what action was taken to complete this corrective action:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Action Taken Description</label>
                    <textarea
                        v-model="completeActionForm.action_taken_description"
                        rows="4"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        :class="{ 'border-red-500': completeActionForm.errors.action_taken_description }"
                        placeholder="Describe what was done to address this corrective action..."
                    ></textarea>
                    <p v-if="completeActionForm.errors.action_taken_description" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ completeActionForm.errors.action_taken_description }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showCompleteActionDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="completeCorrectiveAction"
                        :disabled="completeActionForm.processing || !completeActionForm.action_taken_description"
                        class="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50"
                    >
                        Complete Action
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Complete Quick Fix Dialog -->
        <Modal :show="showCompleteQuickFixDialog" @close="showCompleteQuickFixDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Complete Quick Fix Action</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Provide notes about the completed action:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Completion Notes</label>
                    <textarea
                        v-model="completeQuickFixForm.completion_notes"
                        rows="4"
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-green-500 focus:ring-green-500"
                        :class="{ 'border-red-500': completeQuickFixForm.errors.completion_notes }"
                        placeholder="Describe what was done to complete this action..."
                    ></textarea>
                    <p v-if="completeQuickFixForm.errors.completion_notes" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ completeQuickFixForm.errors.completion_notes }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showCompleteQuickFixDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="completeQuickFix"
                        :disabled="completeQuickFixForm.processing || !completeQuickFixForm.completion_notes"
                        class="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50"
                    >
                        Complete Action
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Close Quick Fix Dialog -->
        <Modal :show="showCloseQuickFixDialog" @close="showCloseQuickFixDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Close Incident - Quick Fix Completed</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Confirm that the quick fix action has been reviewed and is satisfactory. Provide any final notes:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Closure Notes</label>
                    <textarea
                        v-model="closeQuickFixForm.closure_notes"
                        rows="4"
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-green-500 focus:ring-green-500"
                        :class="{ 'border-red-500': closeQuickFixForm.errors.closure_notes }"
                        placeholder="Enter any final notes about the resolution..."
                    ></textarea>
                    <p v-if="closeQuickFixForm.errors.closure_notes" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ closeQuickFixForm.errors.closure_notes }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showCloseQuickFixDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="closeQuickFix"
                        :disabled="closeQuickFixForm.processing"
                        class="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50"
                    >
                        Close Incident
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Redo Fix Dialog -->
        <Modal :show="showRedoFixDialog" @close="showRedoFixDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Redo Quick Fix Action</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">The quick fix action will be sent back for rework. Provide feedback for the assignee:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Feedback / Reason for Redo <span class="text-red-500">*</span></label>
                    <textarea
                        v-model="redoFixForm.review_notes"
                        rows="4"
                        class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-orange-500 focus:ring-orange-500"
                        :class="{ 'border-red-500': redoFixForm.errors.review_notes }"
                        placeholder="Explain what needs to be corrected or redone..."
                    ></textarea>
                    <p v-if="redoFixForm.errors.review_notes" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ redoFixForm.errors.review_notes }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showRedoFixDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="redoFix"
                        :disabled="redoFixForm.processing || !redoFixForm.review_notes"
                        class="px-4 py-2 bg-orange-600 dark:bg-orange-500 text-white rounded-md hover:bg-orange-700 dark:hover:bg-orange-600 disabled:opacity-50"
                    >
                        Send for Rework
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Require Further Work Dialog -->
        <Modal :show="showRequireFurtherWorkDialog" @close="showRequireFurtherWorkDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Require Further Work on Corrective Actions</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Provide feedback on what needs to be corrected or improved:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Review Notes <span class="text-red-500">*</span></label>
                    <textarea
                        v-model="requireFurtherWorkForm.corrective_action_review_note"
                        rows="4"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                        :class="{ 'border-red-500': requireFurtherWorkForm.errors.corrective_action_review_note }"
                        placeholder="Explain what needs to be corrected or improved..."
                    ></textarea>
                    <p v-if="requireFurtherWorkForm.errors.corrective_action_review_note" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ requireFurtherWorkForm.errors.corrective_action_review_note }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showRequireFurtherWorkDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="requireFurtherWork"
                        :disabled="requireFurtherWorkForm.processing || !requireFurtherWorkForm.corrective_action_review_note"
                        class="px-4 py-2 bg-yellow-600 dark:bg-yellow-500 text-white rounded-md hover:bg-yellow-700 dark:hover:bg-yellow-600 disabled:opacity-50"
                    >
                        Send for Rework
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Approve and Close Corrective Actions Dialog -->
        <Modal :show="showApproveAndCloseCorrectiveActionsDialog" @close="showApproveAndCloseCorrectiveActionsDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Approve and Close Incident</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">All corrective actions will be approved and the incident will be closed. Provide closure notes:</p>

                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Closure Notes <span class="text-red-500">*</span></label>
                    <textarea
                        v-model="approveAndCloseCorrectiveActionsForm.closure_notes"
                        rows="4"
                        class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-green-500 focus:ring-green-500"
                        :class="{ 'border-red-500': approveAndCloseCorrectiveActionsForm.errors.closure_notes }"
                        placeholder="Provide closure notes for the incident..."
                    ></textarea>
                    <p v-if="approveAndCloseCorrectiveActionsForm.errors.closure_notes" class="mt-1 text-sm text-red-600 dark:text-red-400">
                        {{ approveAndCloseCorrectiveActionsForm.errors.closure_notes }}
                    </p>
                </div>

                <div class="mt-4 flex justify-end space-x-3">
                    <button
                        @click="showApproveAndCloseCorrectiveActionsDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="approveAndCloseCorrectiveActions"
                        :disabled="approveAndCloseCorrectiveActionsForm.processing || !approveAndCloseCorrectiveActionsForm.closure_notes"
                        class="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50"
                    >
                        Approve and Close
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Print PDF Dialog -->
        <Modal :show="showPrintPdfDialog" @close="showPrintPdfDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Print Incident PDF</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Generate a PDF report for this incident:</p>

                <div class="mb-4">
                    <label class="flex items-center">
                        <input
                            type="checkbox"
                            v-model="printPdfForm.include_activity_log"
                            class="rounded border-gray-300 dark:border-gray-600 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 dark:bg-gray-700"
                        />
                        <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Include Activity Log</span>
                    </label>
                    <p class="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">Add a complete activity log to the PDF report</p>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="showPrintPdfDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="printPdf"
                        :disabled="printPdfForm.processing"
                        class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
                    >
                        <svg v-if="printPdfForm.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Generate PDF
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Submit Investigation Dialog -->
        <Modal :show="showSubmitInvestigationDialog" @close="showSubmitInvestigationDialog = false">
            <div class="p-6">
                <div class="flex items-center mb-4">
                    <div class="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    <h3 class="ml-4 text-lg font-semibold text-gray-900 dark:text-white">Submit Investigation for Review</h3>
                </div>

                <div class="space-y-3">
                    <p class="text-sm text-gray-700 dark:text-gray-300">
                        Are you sure you have completed the investigation and want to submit it for manager review?
                    </p>

                    <div class="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-500 p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                </svg>
                            </div>
                            <div class="ml-3">
                                <p class="text-sm text-yellow-700 dark:text-yellow-300 font-medium">Important Notice:</p>
                                <ul class="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1">
                                    <li>Once submitted, you will <strong>not be able to edit</strong> the investigation</li>
                                    <li>You can only make changes if the reviewer returns it back to you</li>
                                    <li>The incident status will change to <strong>"Investigation Completed"</strong></li>
                                    <li>Please ensure all findings, evidence, and root causes are documented</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <p class="text-sm text-gray-600 dark:text-gray-400">
                        Do you want to proceed with the submission?
                    </p>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="showSubmitInvestigationDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        @click="submitInvestigation"
                        class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 font-medium"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Confirm & Submit
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Reopen Investigation Dialog -->
        <Modal :show="showReopenInvestigationDialog" @close="showReopenInvestigationDialog = false">
            <div class="p-6">
                <div class="flex items-center mb-4">
                    <div class="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <svg class="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z"></path>
                        </svg>
                    </div>
                    <h3 class="ml-4 text-lg font-semibold text-gray-900 dark:text-white">Requires Further Investigation</h3>
                </div>

                <div class="space-y-4">
                    <p class="text-sm text-gray-700 dark:text-gray-300">
                        Please provide a reason for reopening this investigation. The investigator will be notified and can make additional changes.
                    </p>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Reason for Reopening *</label>
                        <textarea
                            v-model="reopenInvestigationForm.reopen_reason"
                            rows="4"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-orange-500 focus:ring-orange-500 resize-y"
                            placeholder="Explain what needs to be investigated further..."
                        ></textarea>
                        <p v-if="reopenInvestigationForm.errors.reopen_reason" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ reopenInvestigationForm.errors.reopen_reason }}
                        </p>
                    </div>

                    <div class="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 dark:border-blue-500 p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                                </svg>
                            </div>
                            <div class="ml-3">
                                <p class="text-sm text-blue-700 dark:text-blue-300">
                                    The incident status will change back to <strong>"Under Investigation"</strong> and the investigator will be able to edit again.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="showReopenInvestigationDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        @click="submitReopenInvestigation"
                        :disabled="!reopenInvestigationForm.reopen_reason || reopenInvestigationForm.processing"
                        class="inline-flex items-center px-4 py-2 bg-orange-600 dark:bg-orange-500 text-white rounded-md hover:bg-orange-700 dark:hover:bg-orange-600 font-medium disabled:opacity-50"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Confirm & Reopen
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Approve & Close Dialog -->
        <Modal :show="showApproveAndCloseDialog" @close="showApproveAndCloseDialog = false">
            <div class="p-6">
                <div class="flex items-center mb-4">
                    <div class="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    <h3 class="ml-4 text-lg font-semibold text-gray-900 dark:text-white">Approve Investigation & Close Incident</h3>
                </div>

                <div class="space-y-3">
                    <p class="text-sm text-gray-700 dark:text-gray-300">
                        You are about to approve this investigation and close the incident. Please review carefully before proceeding.
                    </p>

                    <div class="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-500 p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                </svg>
                            </div>
                            <div class="ml-3">
                                <p class="text-sm text-yellow-700 dark:text-yellow-300 font-medium">Critical Warning:</p>
                                <ul class="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1">
                                    <li>Once closed, the investigation <strong>cannot be reopened</strong></li>
                                    <li><strong>No further investigation</strong> or corrective actions can be added</li>
                                    <li>This maintains the <strong>integrity of data</strong> collected for the incident</li>
                                    <li>If corrective actions are needed, use <strong>"Approve & Add Corrective Action"</strong></li>
                                    <li>If reinvestigation is required, use <strong>"Requires Further Investigation"</strong></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Closure Notes *</label>
                        <textarea
                            v-model="approveAndCloseForm.closure_notes"
                            rows="4"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-green-500 focus:ring-green-500 resize-y"
                            placeholder="Provide final closure notes for this incident..."
                        ></textarea>
                        <p v-if="approveAndCloseForm.errors.closure_notes" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ approveAndCloseForm.errors.closure_notes }}
                        </p>
                        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Document the final outcome and any relevant closing information
                        </p>
                    </div>

                    <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">
                        Do you want to proceed with approving and closing this incident?
                    </p>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="showApproveAndCloseDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        @click="submitApproveAndClose"
                        :disabled="!approveAndCloseForm.closure_notes || approveAndCloseForm.processing"
                        class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 font-medium disabled:opacity-50"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        Approve & Close Incident
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Edit Investigation Item Dialog -->
        <Modal :show="showEditItemDialog" @close="showEditItemDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Investigation Item</h3>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                        <select
                            v-model="editItemForm.item_type"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500"
                        >
                            <option value="investigation_note">Note</option>
                            <option value="evidence">Evidence</option>
                            <option value="witness_statement">Witness Statement</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                        <input
                            v-model="editItemForm.title"
                            type="text"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500"
                        />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                        <textarea
                            v-model="editItemForm.description"
                            rows="4"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 resize-y"
                        ></textarea>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">File Attachment</label>

                        <!-- Show current file if exists -->
                        <div v-if="editItemForm.current_attachment" class="mb-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md border border-gray-200 dark:border-gray-700">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center space-x-2">
                                    <svg class="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                    </svg>
                                    <span class="text-sm text-gray-700 dark:text-gray-300">{{ editItemForm.current_attachment.file_name }}</span>
                                </div>
                                <span class="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">Current File</span>
                            </div>
                        </div>

                        <!-- File upload input -->
                        <input
                            @change="handleEditFileUpload"
                            type="file"
                            :disabled="editItemForm.current_attachment !== null"
                            class="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-2 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-purple-50 dark:file:bg-purple-900/30 file:text-purple-700 dark:file:text-purple-300 hover:file:bg-purple-100 dark:hover:file:bg-purple-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
                            :class="{ 'cursor-not-allowed': editItemForm.current_attachment !== null }"
                        />

                        <p v-if="editItemForm.current_attachment" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            A file is already attached. Delete the current file first to upload a new one.
                        </p>
                        <p v-else class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Upload a new file (optional)
                        </p>
                    </div>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="showEditItemDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="updateInvestigationItem"
                        :disabled="!editItemForm.title || editItemForm.processing"
                        class="px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50"
                    >
                        Update
                    </button>
                </div>
            </div>
        </Modal>

        <!-- Edit Root Cause Dialog -->
        <Modal :show="showEditRootCauseDialog" @close="showEditRootCauseDialog = false">
            <div class="p-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Root Cause</h3>

                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                        <select
                            v-model="editRootCauseForm.cause_type"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500"
                        >
                            <option value="primary">Primary</option>
                            <option value="contributing">Contributing</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                        <textarea
                            v-model="editRootCauseForm.description"
                            rows="4"
                            class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 resize-y"
                        ></textarea>
                    </div>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        @click="showEditRootCauseDialog = false"
                        class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="updateRootCause"
                        :disabled="!editRootCauseForm.description || editRootCauseForm.processing"
                        class="px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white rounded-md hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50"
                    >
                        Update
                    </button>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, usePage, router } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import Modal from '@/Components/Common/Modal.vue';

const page = usePage();

const props = defineProps({
    incident: {
        type: Object,
        required: true,
    },
    canTriage: {
        type: Boolean,
        default: false,
    },
    canEdit: {
        type: Boolean,
        default: false,
    },
    canClose: {
        type: Boolean,
        default: false,
    },
    isInvestigationMember: {
        type: Boolean,
        default: false,
    },
    hasAssignedActions: {
        type: Boolean,
        default: false,
    },
    isReporter: {
        type: Boolean,
        default: false,
    },
    isQuickFixAssignee: {
        type: Boolean,
        default: false,
    },
    logs: {
        type: Array,
        default: () => [],
    },
    investigationStats: {
        type: Object,
        default: null,
    },
    users: {
        type: Array,
        default: () => [],
    },
});

// Use timezone composable for date formatting
const { formatDateTime: formatDateTimeInTZ } = useTimezone();

const showCompleteQuickFixDialog = ref(false);
const showChangeAssigneeDialog = ref(false);
const showCloseQuickFixDialog = ref(false);
const showRedoFixDialog = ref(false);
const showChangeInvestigationAssigneeDialog = ref(false);
const showChangeCorrectiveActionAssigneeDialog = ref(false);
const showCompleteActionDialog = ref(false);
const showRequireFurtherWorkDialog = ref(false);
const showApproveAndCloseCorrectiveActionsDialog = ref(false);
const showPrintPdfDialog = ref(false);
const selectedAction = ref(null);

const changeAssigneeForm = useSecureForm({
    quick_fix_assigned_to: '',
});

const changeInvestigationAssigneeForm = useSecureForm({
    investigation_assigned_to: '',
});

const changeCorrectiveActionAssigneeForm = useSecureForm({
    corrective_action_assigned_to: '',
});

const completeActionForm = useSecureForm({
    action_id: null,
    action_taken_description: '',
});

const requireFurtherWorkForm = useSecureForm({
    corrective_action_review_note: '',
});

const approveAndCloseCorrectiveActionsForm = useSecureForm({
    closure_notes: '',
});

const printPdfForm = ref({
    include_activity_log: false,
    processing: false,
});

const completeQuickFixForm = useSecureForm({
    completion_notes: '',
});

const closeQuickFixForm = useSecureForm({
    closure_notes: '',
});

const redoFixForm = useSecureForm({
    review_notes: '',
});

const changeAssignee = () => {
    changeAssigneeForm.put(route('incidents.quick-fix.change-assignee', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            showChangeAssigneeDialog.value = false;
            changeAssigneeForm.reset();
        },
    });
};

const changeInvestigationAssignee = () => {
    changeInvestigationAssigneeForm.put(route('incidents.investigation.change-assignee', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            showChangeInvestigationAssigneeDialog.value = false;
            changeInvestigationAssigneeForm.reset();
        },
    });
};

const changeCorrectiveActionAssignee = () => {
    changeCorrectiveActionAssigneeForm.put(route('incidents.corrective-actions.change-assignee', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            showChangeCorrectiveActionAssigneeDialog.value = false;
            changeCorrectiveActionAssigneeForm.reset();
        },
    });
};

const completeQuickFix = () => {
    completeQuickFixForm.post(route('incidents.quick-fix.complete', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            showCompleteQuickFixDialog.value = false;
            completeQuickFixForm.reset();
        },
    });
};

const closeQuickFix = () => {
    closeQuickFixForm.post(route('incidents.quick-fix.close', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            showCloseQuickFixDialog.value = false;
            closeQuickFixForm.reset();
        },
    });
};

const redoFix = () => {
    redoFixForm.post(route('incidents.quick-fix.redo', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            showRedoFixDialog.value = false;
            redoFixForm.reset();
        },
    });
};

const startInvestigation = () => {
    router.post(route('incidents.investigation.start', props.incident.id), {
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            // Investigation workspace will now be visible
        },
    });
};

// Investigation workspace forms and functions
const rootCauseForm = useSecureForm({
    cause_type: 'primary',
    description: '',
    processing: false,
});

const investigationItemForm = useSecureForm({
    item_type: 'investigation_note',
    title: '',
    description: '',
    file: null,
    processing: false,
});

const showSubmitInvestigationDialog = ref(false);
const showReopenInvestigationDialog = ref(false);
const showApproveAndCloseDialog = ref(false);
const showEditItemDialog = ref(false);
const showEditRootCauseDialog = ref(false);

const editItemForm = useSecureForm({
    id: null,
    item_type: 'investigation_note',
    title: '',
    description: '',
    file: null,
    current_attachment: null,
    processing: false,
});

const editRootCauseForm = useSecureForm({
    id: null,
    cause_type: 'primary',
    description: '',
    processing: false,
});

const reopenInvestigationForm = useSecureForm({
    reopen_reason: '',
    processing: false,
});

const approveAndCloseForm = useSecureForm({
    closure_notes: '',
    processing: false,
});

const correctiveActionForm = useSecureForm({
    action_type: '',
    title: '',
    description: '',
    processing: false,
});

const handleFileUpload = (event) => {
    investigationItemForm.file = event.target.files[0];
};

const handleEditFileUpload = (event) => {
    editItemForm.file = event.target.files[0];
};

const openEditItemModal = (item) => {
    editItemForm.id = item.id;
    editItemForm.item_type = item.item_type;
    editItemForm.title = item.title;
    editItemForm.description = item.description || '';
    editItemForm.file = null;
    editItemForm.current_attachment = item.attachment || null;
    showEditItemDialog.value = true;
};

const openEditRootCauseModal = (cause) => {
    editRootCauseForm.id = cause.id;
    editRootCauseForm.cause_type = cause.cause_type;
    editRootCauseForm.description = cause.description;
    showEditRootCauseDialog.value = true;
};

const addRootCause = () => {
    rootCauseForm.processing = true;
    rootCauseForm.post(route('incidents.investigation.root-causes.add', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            rootCauseForm.reset();
            rootCauseForm.cause_type = 'primary';
            rootCauseForm.processing = false;
        },
        onError: () => {
            rootCauseForm.processing = false;
        },
    });
};

const deleteRootCause = (rootCauseId) => {
    if (!confirm('Are you sure you want to delete this root cause?')) return;

    router.delete(route('incidents.investigation.root-causes.delete', {
        incident: props.incident.id,
        rootCause: rootCauseId
    }), {
        data: {
            _company_id: page.props.company_id
        },
        preserveScroll: true,
    });
};

const addInvestigationItem = () => {
    investigationItemForm.processing = true;
    investigationItemForm.post(route('incidents.investigation.items.add', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            investigationItemForm.reset();
            investigationItemForm.item_type = 'investigation_note';
            investigationItemForm.processing = false;
            // Reset file input
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput) fileInput.value = '';
        },
        onError: () => {
            investigationItemForm.processing = false;
        },
    });
};

const deleteInvestigationItem = (itemId) => {
    if (!confirm('Are you sure you want to delete this investigation item?')) return;

    router.delete(route('incidents.investigation.items.delete', {
        incident: props.incident.id,
        item: itemId
    }), {
        data: {
            _company_id: page.props.company_id
        },
        preserveScroll: true,
    });
};

const deleteInvestigationFile = (itemId) => {
    if (!confirm('Are you sure you want to delete this file? This action cannot be undone.')) return;

    router.delete(route('incidents.investigation.file.delete', {
        incident: props.incident.id,
        item: itemId
    }), {
        data: {
            _company_id: page.props.company_id
        },
        preserveScroll: true,
    });
};

const submitInvestigation = () => {
    router.post(route('incidents.investigation.submit', props.incident.id), {
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showSubmitInvestigationDialog.value = false;
        },
    });
};

const handleRequiresFurtherInvestigation = () => {
    showReopenInvestigationDialog.value = true;
};

const submitReopenInvestigation = () => {
    reopenInvestigationForm.processing = true;
    router.post(route('incidents.investigation.review', props.incident.id), {
        approved: false,
        reopen_reason: reopenInvestigationForm.reopen_reason,
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showReopenInvestigationDialog.value = false;
            reopenInvestigationForm.reset();
            reopenInvestigationForm.processing = false;
        },
        onError: (errors) => {
            reopenInvestigationForm.processing = false;
            reopenInvestigationForm.errors = errors;
        },
    });
};

const handleApproveAndClose = () => {
    showApproveAndCloseDialog.value = true;
};

const submitApproveAndClose = () => {
    approveAndCloseForm.processing = true;
    router.post(route('incidents.investigation.approve-and-close', props.incident.id), {
        closure_notes: approveAndCloseForm.closure_notes,
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showApproveAndCloseDialog.value = false;
            approveAndCloseForm.reset();
            approveAndCloseForm.processing = false;
        },
        onError: (errors) => {
            approveAndCloseForm.processing = false;
            approveAndCloseForm.errors = errors;
        },
    });
};

const handleApproveAndAddCorrectiveAction = () => {
    router.post(route('incidents.investigation.approve', props.incident.id), {
        _company_id: page.props.company_id
    }, {
        preserveScroll: false,
        onSuccess: () => {
            // Redirect to corrective actions add page
        },
    });
};

const addCorrectiveAction = () => {
    correctiveActionForm.processing = true;
    correctiveActionForm.post(route('incidents.actions.store', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            correctiveActionForm.reset();
            correctiveActionForm.processing = false;
        },
        onError: () => {
            correctiveActionForm.processing = false;
        },
    });
};

const startCorrectiveAction = (actionId) => {
    router.post(route('incidents.corrective-actions.start', {
        incident: props.incident.id,
        action: actionId
    }), {
        _company_id: page.props.company_id
    }, {
        preserveScroll: true,
    });
};

const openCompleteActionDialog = (action) => {
    selectedAction.value = action;
    completeActionForm.action_id = action.id;
    completeActionForm.action_taken_description = '';
    showCompleteActionDialog.value = true;
};

const completeCorrectiveAction = () => {
    completeActionForm.post(route('incidents.corrective-actions.complete', {
        incident: props.incident.id,
        action: completeActionForm.action_id
    }), {
        preserveScroll: true,
        onSuccess: () => {
            showCompleteActionDialog.value = false;
            completeActionForm.reset();
            selectedAction.value = null;
        },
    });
};

const allCorrectiveActionsCompleted = computed(() => {
    if (!props.incident.corrective_actions || props.incident.corrective_actions.length === 0) {
        return false;
    }
    return props.incident.corrective_actions.every(action => action.status === 'completed');
});

const submitCorrectiveActionsForReview = () => {
    router.post(route('incidents.corrective-actions.submit-for-review', props.incident.id), {
        _company_id: page.props.company_id
    }, {
        preserveScroll: false,
        onSuccess: () => {
            // Status will change to correction_in_review
        },
    });
};

const requireFurtherWork = () => {
    requireFurtherWorkForm.post(route('incidents.corrective-actions.require-further-work', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            showRequireFurtherWorkDialog.value = false;
            requireFurtherWorkForm.reset();
        },
    });
};

const approveAndCloseCorrectiveActions = () => {
    approveAndCloseCorrectiveActionsForm.post(route('incidents.corrective-actions.approve-and-close', props.incident.id), {
        preserveScroll: false,
        onSuccess: () => {
            showApproveAndCloseCorrectiveActionsDialog.value = false;
            approveAndCloseCorrectiveActionsForm.reset();
        },
    });
};

const printPdf = () => {
    printPdfForm.value.processing = true;

    // Create form data
    const url = route('incidents.pdf', props.incident.id);
    const params = new URLSearchParams({
        include_activity_log: printPdfForm.value.include_activity_log ? '1' : '0',
    });

    // Open PDF in new window
    window.open(`${url}?${params.toString()}`, '_blank');

    // Reset and close dialog
    setTimeout(() => {
        printPdfForm.value.processing = false;
        printPdfForm.value.include_activity_log = false;
        showPrintPdfDialog.value = false;
    }, 1000);
};

const updateInvestigationItem = () => {
    editItemForm.processing = true;

    // If there's a file, use POST with _method spoofing for multipart/form-data support
    if (editItemForm.file) {
        const formData = new FormData();
        formData.append('_method', 'PUT');
        formData.append('item_type', editItemForm.item_type);
        formData.append('title', editItemForm.title);
        formData.append('description', editItemForm.description || '');
        formData.append('file', editItemForm.file);
        formData.append('_company_id', page.props.company_id);

        router.post(route('incidents.investigation.items.update', {
            incident: props.incident.id,
            item: editItemForm.id
        }), formData, {
            preserveScroll: true,
            onSuccess: () => {
                showEditItemDialog.value = false;
                editItemForm.reset();
                editItemForm.processing = false;
                // Reset file input
                const fileInputs = document.querySelectorAll('input[type="file"]');
                fileInputs.forEach(input => input.value = '');
            },
            onError: (errors) => {
                editItemForm.processing = false;
                editItemForm.errors = errors;
            },
        });
    } else {
        // No file, use regular PUT request
        editItemForm.put(route('incidents.investigation.items.update', {
            incident: props.incident.id,
            item: editItemForm.id
        }), {
            preserveScroll: true,
            onSuccess: () => {
                showEditItemDialog.value = false;
                editItemForm.reset();
                editItemForm.processing = false;
            },
            onError: () => {
                editItemForm.processing = false;
            },
        });
    }
};

const updateRootCause = () => {
    editRootCauseForm.processing = true;
    editRootCauseForm.put(route('incidents.investigation.root-causes.update', {
        incident: props.incident.id,
        rootCause: editRootCauseForm.id
    }), {
        preserveScroll: true,
        onSuccess: () => {
            showEditRootCauseDialog.value = false;
            editRootCauseForm.reset();
            editRootCauseForm.processing = false;
        },
        onError: () => {
            editRootCauseForm.processing = false;
        },
    });
};

// Computed properties
// Check if user can view full incident details
// General users (without view_all permission) can only see basic incident information
const canViewFullDetails = computed(() => {
    const permissions = page.props.auth?.permissions || [];
    return permissions.includes('incidents.incidents.view_all');
});

// Helper functions
const getStatusClass = (status) => {
    const classes = {
        open: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800',
        triage_completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800',
        assigned: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800',
        action_completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800',
        under_investigation: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800',
        investigation_completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800',
        pending_corrective_action: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800',
        correction_in_progress: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800',
        correction_in_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800',
        pending_review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800',
        closed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800',
    };
    return classes[status] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
};

const getSeverityClass = (severity) => {
    const classes = {
        low: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800',
        medium: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800',
        high: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800',
        critical: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800',
    };
    return classes[severity] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800',
        normal: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800',
        medium: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800',
        high: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800',
        urgent: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800',
        critical: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800',
    };
    return classes[priority] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
};

const getActionStatusClass = (status) => {
    const classes = {
        pending: 'bg-gray-100 text-gray-800',
        in_progress: 'bg-blue-100 text-blue-800',
        completed: 'bg-green-100 text-green-800',
        verified: 'bg-purple-100 text-purple-800',
        cancelled: 'bg-red-100 text-red-800',
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
};

const getLogIconClass = (actionType) => {
    const classes = {
        created: 'bg-green-500',
        triaged: 'bg-purple-500',
        assigned: 'bg-blue-500',
        investigation_assigned: 'bg-purple-600',
        investigation_started: 'bg-indigo-500',
        investigation_item_added: 'bg-indigo-400',
        investigation_item_updated: 'bg-indigo-500',
        investigation_submitted: 'bg-purple-600',
        investigation_approved: 'bg-green-600',
        investigation_reopened: 'bg-orange-500',
        corrective_actions_created: 'bg-teal-500',
        corrective_action_implementation_started: 'bg-blue-600',
        corrective_action_added: 'bg-cyan-500',
        corrective_action_completed: 'bg-green-600',
        corrective_actions_submitted_for_review: 'bg-purple-600',
        corrective_actions_approved: 'bg-emerald-600',
        corrective_actions_reopened: 'bg-orange-600',
        action_assigned: 'bg-yellow-500',
        action_completed: 'bg-green-500',
        action_verified: 'bg-purple-500',
        status_changed: 'bg-blue-500',
        closed: 'bg-green-800',
        reopened: 'bg-orange-500',
    };
    return classes[actionType] || 'bg-gray-500';
};

// Format date in company timezone
const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date); // Now timezone-aware using company timezone!
};

// Format date and time in company timezone
const formatDateTime = (datetime) => {
    if (!datetime) return 'N/A';
    return formatDateTimeInTZ(datetime); // Now timezone-aware using company timezone!
};

// Get time remaining for SLA
const getTimeRemaining = (dueAt) => {
    if (!dueAt) return '';
    const now = new Date();
    const due = new Date(dueAt);
    const diff = due - now;

    if (diff < 0) {
        const hours = Math.floor(Math.abs(diff) / (1000 * 60 * 60));
        return `Overdue by ${hours}h`;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
        return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
};

const formatClosureReason = (reason) => {
    const reasons = {
        duplicate: 'Duplicate',
        quick_fix: 'Quick Fix',
        no_action_required: 'No Action Required',
        insufficient_info: 'Insufficient Information',
        out_of_scope: 'Out of Scope',
        resolved_by_reporter: 'Resolved by Reporter',
    };
    return reasons[reason] || reason;
};
</script>
